import React, { useCallback, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import AdminPriceTraceModal from "./AdminPriceTraceModal";
import { canShowAdminPriceTrace } from "../adminPriceTraceFlags";

const MAX_CACHE_ENTRIES = 40;

export default function AdminPriceTraceTrigger({
  entityType = "hotel",
  entityId,
  entityName,
  tracePayload,
  onRequestTrace,
  onOpenTrace,
  buttonVariant = "text",
  size = "small",
  cacheRevision,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trace, setTrace] = useState(null);
  const cacheRef = useRef(new Map());

  const openWithPayload = useCallback((payload) => {
    if (!payload) return;
    setTrace(payload);
    setOpen(true);
  }, []);

  const handleClick = useCallback(async () => {
    if (!canShowAdminPriceTrace()) return;

    if (tracePayload) {
      openWithPayload(tracePayload);
      if (typeof onOpenTrace === "function") {
        try {
          onOpenTrace(tracePayload);
        } catch {
          /* ignore */
        }
      }
      return;
    }
    if (typeof onRequestTrace !== "function") return;

    const ck = `${entityType}:${entityId}:${cacheRevision ?? "0"}`;
    const hit = cacheRef.current.get(ck);
    if (hit) {
      openWithPayload(hit);
      if (typeof onOpenTrace === "function") {
        try {
          onOpenTrace(hit);
        } catch {
          /* ignore */
        }
      }
      return;
    }

    setLoading(true);
    try {
      const data = await onRequestTrace();
      if (!data) return;
      if (cacheRef.current.size >= MAX_CACHE_ENTRIES) {
        const first = cacheRef.current.keys().next().value;
        cacheRef.current.delete(first);
      }
      cacheRef.current.set(ck, data);
      openWithPayload(data);
      if (typeof onOpenTrace === "function") {
        try {
          onOpenTrace(data);
        } catch {
          /* ignore */
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    cacheRevision,
    entityId,
    entityType,
    onRequestTrace,
    onOpenTrace,
    openWithPayload,
    tracePayload,
  ]);

  if (!canShowAdminPriceTrace()) {
    return null;
  }

  const common = {
    size,
    loading,
    onClick: handleClick,
    "aria-label": "Trace מחיר",
    className: "apt-trigger",
  };

  let control = null;
  if (buttonVariant === "icon") {
    control = <Button type="text" {...common} icon={<SearchOutlined />} />;
  } else if (buttonVariant === "outlined") {
    control = (
      <Button {...common} type="default">
        Trace
      </Button>
    );
  } else {
    control = (
      <Button {...common} type="link" style={{ padding: 0, height: "auto", lineHeight: 1.2 }}>
        Trace
      </Button>
    );
  }

  return (
    <>
      <Tooltip title="Trace מחיר">{control}</Tooltip>
      <AdminPriceTraceModal open={open} onClose={() => setOpen(false)} trace={trace} />
    </>
  );
}
